import React from "react"
import { Provider as StyletronProvider } from "styletron-react"
import { BaseProvider } from "baseui"
import { HashRouter as Router, Switch, Route } from "react-router-dom"
import { PersonalDetailsForm } from "./pages/PersonalDetailsForm"
import { EmploymentDetailsForm, JobType } from "./pages/EmploymentDetailsForm"
import { CoverageLookupForm } from "./pages/CoverageLookupForm"
import { CompletePage } from "./pages/CompletePage"
import { ExpiredPage } from "./pages/ExpiredPage"
import { VolunteerDetailsForm } from "./pages/VolunteerDetailsForm"
import { Value } from "baseui/select"
import { AnalyticsTracker } from "./analytics"
import { PaymentForm } from "./pages/PaymentForm"
import { UnionForm } from "./pages/UnionForm"
import { getConstant } from "./constants"
import { theme, engine } from "./configurables"

export interface Person {
  full_name?: string
  email?: string
  address?: {
    label: string
    value?: Value
  }
  job_search?: {
    label: string
    value: Value | null | undefined
  }
  branch_id?: string | null
  suburb?: string | null
  postcode?: string
  volunteer?: boolean
  join?: boolean
  mobile?: string
  gender?: "f" | "m" | "o" | "n"
  date_of_birth?: Date[]
  job_title?: string
  duties?: string
  employer?: string
  agree?: boolean
  person_with_disability?: boolean
  lgbtiqa?: boolean
  atsi?: boolean
  other_language?: boolean
  industry_1?: Value | null
  industry_2?: Value | null
  industry_3?: Value | null
  industry_4?: Value | null
  leafletsActionTalking?: boolean
  campaignNews?: boolean
  utm_campaign?: string | null
  utm_medium?: string | null
  utm_source?: string | null
  utm_content?: string | null
  referer?: string | null
  source?: string | null
  person_id?: string
  people_id?: string
  bsb?: string
  accountNo?: string
  accountName?: string
  hours_per_week?: number
  // Not sent to API, just used within form
  employer_address?: string | null
  employer_postcode?: string | null
  employer_suburb?: string | null
  pay?: number
  job_type?: JobType
  cost?: number
  payment_type?: "cc" | "dd"
  cant_find_job?: boolean
  last_work_details_path?: string
}

function App({
  volunteerOnly,
  coverageState,
  full_name,
  email,
  mobile,
  utm_campaign,
  utm_medium,
  utm_source,
  utm_content,
  referer,
  source
}: {
  volunteerOnly: boolean
  coverageState?: string | null
  full_name?: string
  email?: string
  mobile?: string
  utm_campaign?: string | null
  utm_medium?: string | null
  utm_source?: string | null
  utm_content?: string | null
  source?: string | null
  referer?: string | null
}) {
  const coverageOnly = getConstant("coverageOnly")
  const disableSessionStorage = getConstant("disableSessionStorage")
  let initalState: Person = {
    volunteer: !!volunteerOnly,
    join: !getConstant("volunteerEnabled") && !volunteerOnly && !coverageOnly,
    full_name,
    email,
    mobile,
    utm_campaign,
    utm_medium,
    utm_source,
    utm_content,
    referer,
    source
  }
  if (!volunteerOnly && !coverageOnly && !disableSessionStorage) {
    const joinSessionStorage = sessionStorage.getItem("join")
    if (joinSessionStorage) {
      initalState = JSON.parse(joinSessionStorage)
      if (initalState.date_of_birth) {
        initalState.date_of_birth = initalState.date_of_birth.map(date => {
          return new Date(date)
        })
      }
    }
  }
  const [person, setPerson] = React.useState<Person>(initalState)
  React.useEffect(() => {
    if (!disableSessionStorage) {
      if (!person.people_id) {
        sessionStorage.setItem("join", "")
      } else {
        sessionStorage.setItem("join", JSON.stringify(person))
      }
    }
  }, [person, disableSessionStorage])
  const resetState = React.useCallback(() => {
    setPerson({
      volunteer: !!volunteerOnly,
      join: !getConstant("volunteerEnabled") && !volunteerOnly && !coverageOnly,
      full_name,
      email,
      mobile,
      utm_campaign,
      utm_medium,
      utm_source,
      utm_content,
      referer,
      source
    })
  }, [
    setPerson,
    full_name,
    email,
    mobile,
    utm_campaign,
    utm_medium,
    utm_source,
    utm_content,
    referer,
    source,
    volunteerOnly,
    coverageOnly
  ])
  return (
    <Router>
      <AnalyticsTracker person={person} />
      <StyletronProvider value={engine}>
        <BaseProvider theme={theme}>
          <Switch>
            <Route path="/" exact>
              <PersonalDetailsForm
                volunteerOnly={volunteerOnly}
                setPerson={setPerson}
                person={person}
              />
            </Route>
            <Route path="/employment-details" exact>
              <EmploymentDetailsForm
                setPerson={setPerson}
                person={person}
                manual={false}
              />
            </Route>
            <Route path="/manual-employment-details" exact>
              <EmploymentDetailsForm
                setPerson={setPerson}
                person={person}
                manual={true}
              />
            </Route>
            <Route path="/union" exact>
              <UnionForm person={person} setPerson={setPerson} />
            </Route>
            <Route path="/payment" exact>
              <PaymentForm person={person} setPerson={setPerson} />
            </Route>
            <Route path="/volunteer-details" exact>
              <VolunteerDetailsForm
                volunteerOnly={volunteerOnly}
                person={person}
                setPerson={setPerson}
              />
            </Route>
            <Route path="/complete" exact>
              <CompletePage
                volunteerOnly={volunteerOnly}
                resetState={resetState}
                setPerson={setPerson}
                person={person}
              />
            </Route>
            <Route path="/expired" exact>
              <ExpiredPage
                volunteerOnly={volunteerOnly}
                resetState={resetState}
                setPerson={setPerson}
                person={person}
              />
            </Route>
            <Route path="/coverage" exact>
              <CoverageLookupForm
                setPerson={setPerson}
                person={person}
                manual={false}
                coverageState={coverageState}
              />
            </Route>
            <Route path="/manual-coverage" exact>
              <CoverageLookupForm
                setPerson={setPerson}
                person={person}
                manual={true}
                coverageState={coverageState}
              />
            </Route>
          </Switch>
        </BaseProvider>
      </StyletronProvider>
    </Router>
  )
}

export default App
